import React, { useState, useEffect } from "react";
import { FaQuoteLeft, FaUser, FaArrowLeft, FaSearch } from "react-icons/fa";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { reviews } from "../shared/reviewsData";

const ReviewCard = ({ testimonial }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150;
  const isLongTestimonial = testimonial.testimonial.length > maxLength;

  const getDisplayText = () => {
    if (!isLongTestimonial || isExpanded) {
      return testimonial.testimonial;
    }
    return (
      testimonial.testimonial.substr(
        0,
        testimonial.testimonial.lastIndexOf(" ", maxLength)
      ) + "..."
    );
  };

  return (
    <div className="relative group h-full">
      {/* Card glow effect */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-blue-500/10 
                    rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      />

      {/* Main card container */}
      <div
        className="relative flex flex-col h-full backdrop-blur-xl bg-white/5 p-8 rounded-2xl
                    border border-white/10 group-hover:border-white/20
                    transition-all duration-200 hover:transform hover:scale-[1.01]"
      >
        {/* Content */}
        <div className="flex-grow">
          <FaQuoteLeft className="text-teal-400 text-3xl mb-6" />
          <div className="text-gray-300 text-lg leading-relaxed">
            <p>{getDisplayText()}</p>
            {isLongTestimonial && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-teal-400 hover:text-teal-300 mt-2 text-sm font-medium transition-colors duration-200"
              >
                {isExpanded ? "Show Less" : "Read More"}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="flex items-center">
            <div
              className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-400 to-blue-400 
                          flex items-center justify-center"
            >
              <FaUser className="text-white text-xl" />
            </div>
            <div className="ml-4 flex-grow">
              <h4 className="text-white font-semibold">{testimonial.name}</h4>
              <p className="text-teal-400 text-sm">{testimonial.location}</p>
            </div>
            <div className="flex gap-1">
              {[...Array(testimonial.rating)].map((_, i) => (
                <span key={i} className="text-amber-400">
                  ★
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AllReviews = () => {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    { id: "all", label: "All Reviews" },
    { id: "schengen", label: "Schengen Visa" },
    { id: "uk", label: "UK Visa" },
    { id: "usa", label: "USA Visa" },
    { id: "japan", label: "Japan Visa" },
    { id: "multiple", label: "Multiple Visas" },
    { id: "other", label: "Other Visas" },
  ];

  // ✅ Sort (descending) and then filter
  const filteredTestimonials = [...reviews]
    .sort((a, b) => Number(b.id) - Number(a.id))
    .filter((testimonial) => {
      if (!testimonial.type && filter !== "all") {
        console.log(`Testimonial from ${testimonial.name} has no type defined`);
        return false;
      }

      const matchesFilter = filter === "all" || testimonial.type === filter;
      const matchesSearch =
        searchTerm === "" ||
        testimonial.testimonial
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        testimonial.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.location.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gradient-to-br from-[#1a1f35] to-[#111827] min-h-screen pt-24 pb-20">
      <Helmet>
        <title>Client Success Stories | Viszapp</title>
        <meta
          name="description"
          content="Read real client testimonials and visa success stories from Viszapp customers around the world."
        />
        <link rel="canonical" href="https://viszapp.com/all-reviews" />
      </Helmet>

      {/* Background pattern */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute w-full h-full"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)",
            backgroundSize: "32px 32px",
            zIndex: -1,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Header */}
        <div className="flex flex-col items-start mb-12">
          <Link
            to="/"
            className="flex items-center text-teal-400 hover:text-teal-300 mb-6 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            <span>Back to Home</span>
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">
            Client Success Stories
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl">
            Read what our clients have to say about their experience with Viszapp
            and how we've helped them secure visas successfully.
          </p>
        </div>

        {/* Filters + Search */}
        <div className="mb-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                  ${
                    filter === category.id
                      ? "bg-gradient-to-r from-teal-500 to-teal-400 text-white shadow-md"
                      : "bg-white/5 text-gray-300 hover:bg-white/10"
                  }`}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search reviews..."
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg
                      text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500
                      transition-all duration-200"
            />
          </div>
        </div>

        {/* Results count */}
        <div className="mb-8 text-gray-400">
          Showing {filteredTestimonials.length}{" "}
          {filteredTestimonials.length === 1 ? "review" : "reviews"}
          {filter !== "all" &&
            ` for ${categories.find((c) => c.id === filter)?.label || filter}`}
        </div>

        {/* Testimonials grid */}
        {filteredTestimonials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTestimonials.map((testimonial) => (
              <ReviewCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-xl text-gray-300 mb-4">
              No reviews found matching your criteria.
            </p>
            <button
              onClick={() => {
                setFilter("all");
                setSearchTerm("");
              }}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400
                      text-white font-semibold transition-all duration-300"
            >
              View All Reviews
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllReviews;
